import { useCallback, useMemo } from "react";
import { Typography, Stack } from "@altimateai/lego";
import { DateRange } from "@/components/dateRange";
import dayjs from "dayjs";
import { useAiMlServicesSummary, useUnifiedAiMlEndpoints } from "../hooks";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import { useAppState } from "@/modules/app/useAppContext";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { formatCost } from "@/helpers/utils";
import { FlexListSummary } from "@/components/listSummary";
import { TrendChart } from "./components/TrendChart";
import { EndpointsTable } from "./components/EndpointsTabs";
import { calculateTotals, type Granularity } from "./utils";
import { useBreadcrumb } from "@/components/breadcrumbs";
import { DatabricksWorkspaceSelector } from "@/pages/databricks/summary/components";
import { useSelectedWorkspaceParam } from "@/pages/databricks/summary/hooks";
import type { UnifiedAIMLEndpointsSortBy } from "./types";

const VALID_SORT_BY: readonly UnifiedAIMLEndpointsSortBy[] = [
  "endpoint_name",
  "total_cost_usd",
  "total_dbus",
  "service",
  "entity_type",
];
const VALID_GRANULARITIES: readonly Granularity[] = ["day", "week", "month"];

const parseSortBy = (raw: string | null): UnifiedAIMLEndpointsSortBy =>
  VALID_SORT_BY.includes(raw as UnifiedAIMLEndpointsSortBy)
    ? (raw as UnifiedAIMLEndpointsSortBy)
    : "total_cost_usd";

const parseGranularity = (raw: string | null): Granularity =>
  VALID_GRANULARITIES.includes(raw as Granularity) ? (raw as Granularity) : "day";

const AiMlServicesSummary = (): JSX.Element => {
  useBreadcrumb("AI/ML");
  // Default to "Last 28 days" so the DateRange trigger shows the preset label
  // instead of falling through to a custom "MMM D - MMM D" rendering.
  const { startDate, endDate, onUpdate } = useDateFromSearchParams(
    dayjs().subtract(28, "day").toDate(),
    dayjs().toDate()
  );
  const { searchParams, updateSearchParamsByKey } = useFiltersBySearchParams();
  const { currency } = useAppState();
  const maxDate = useMemo(() => new Date(), []);

  const formattedStartDate = dayjs(startDate).format("YYYY-MM-DD");
  const formattedEndDate = dayjs(endDate).format("YYYY-MM-DD");

  const granularity = parseGranularity(searchParams.get("granularity"));
  const [selectedWorkspace] = useSelectedWorkspaceParam();
  const workspaceId = selectedWorkspace || undefined;
  const entityTypeFilter = searchParams.get("entity_type") || "";
  const entityType = entityTypeFilter || undefined;
  // Endpoint-name filter is a multi-select dropdown: values arrive as repeated
  // `endpoint_name` query params, forwarded as an exact any-of filter.
  const endpointNames = searchParams.getAll("endpoint_name");
  const endpointName = endpointNames.length > 0 ? endpointNames : undefined;
  const sortBy = parseSortBy(searchParams.get("sort_by"));
  const sortOrder = searchParams.get("sort_order") === "asc" ? "asc" : "desc";
  const page = Math.max(0, Number(searchParams.get("page") ?? 0));
  const pageSize = Math.max(1, Number(searchParams.get("pageSize") ?? 20));

  const { data, isLoading, isError } = useAiMlServicesSummary({
    start_date: formattedStartDate,
    end_date: formattedEndDate,
    granularity,
    workspace_id: workspaceId,
  });

  const {
    data: endpointsData,
    isFetching: isEndpointsFetching,
    isError: isEndpointsError,
  } = useUnifiedAiMlEndpoints({
    entity_type: entityType,
    endpoint_name: endpointName,
    start_date: formattedStartDate,
    end_date: formattedEndDate,
    workspace_id: workspaceId,
    sort_by: sortBy,
    sort_order: sortOrder,
    limit: pageSize,
    offset: page * pageSize,
  });

  const formatCurrencyValue = useCallback(
    (value: number) => formatCost(value, currency),
    [currency]
  );

  const handleGranularityChange = useCallback(
    (value: Granularity) => {
      updateSearchParamsByKey({ granularity: value });
    },
    [updateSearchParamsByKey]
  );

  const totals = data ? calculateTotals(data) : null;

  const summaryData = useMemo(
    () => [
      {
        label: "Total Usage & Cost",
        value: formatCurrencyValue(totals?.total_cost_usd ?? 0),
      },
      {
        label: "Model Serving",
        value: formatCurrencyValue(totals?.model_serving_cost_usd ?? 0),
      },
      {
        label: "Vector Search",
        value: formatCurrencyValue(totals?.vector_search_cost_usd ?? 0),
      },
      {
        label: "AI Gateway",
        value: formatCurrencyValue(totals?.ai_gateway_cost_usd ?? 0),
      },
    ],
    [totals, formatCurrencyValue]
  );

  if (isError) {
    return (
      <div className="al-p-4">
        <Stack
          direction="column"
          gap="md"
          align="center"
          justify="center"
          className="al-min-h-[400px]"
        >
          <Typography variant="h4" className="al-text-red-500">
            Error loading AI/ML services data
          </Typography>
          <Typography className="al-text-gray-500">
            Please try again later or contact support if the problem persists.
          </Typography>
        </Stack>
      </div>
    );
  }

  const header = (
    <Stack direction="row" align="center" justify="between">
      <Typography variant="page-heading">AI/ML Services</Typography>
      <Stack direction="row" align="center" gap="sm">
        <DatabricksWorkspaceSelector />
        <DateRange
          startDate={startDate}
          endDate={endDate}
          onDateRangeSelect={onUpdate}
          id="aiml-services-date-range"
          maxDate={maxDate}
        />
      </Stack>
    </Stack>
  );

  if (!isLoading && data && data.length === 0) {
    return (
      <div className="al-p-4">
        <Stack direction="column" gap="md">
          {header}
          <Stack
            direction="column"
            gap="sm"
            align="center"
            justify="center"
            className="al-min-h-[400px]"
          >
            <Typography variant="h4" className="al-text-gray-500">
              No data for selected period
            </Typography>
            <Typography className="al-text-gray-400">
              Try selecting a different date range
            </Typography>
          </Stack>
        </Stack>
      </div>
    );
  }

  return (
    <div className="al-p-4">
      <Stack direction="column" gap="md">
        {header}

        <FlexListSummary isLoading={isLoading} summary={summaryData} />

        <TrendChart
          data={data ?? []}
          isLoading={isLoading}
          currency={currency}
          granularity={granularity}
          onGranularityChange={handleGranularityChange}
        />

        <EndpointsTable
          endpoints={endpointsData?.endpoints ?? []}
          totalCount={endpointsData?.pagination.total ?? 0}
          isLoading={isEndpointsFetching}
          isError={isEndpointsError}
          entityTypeFilter={entityTypeFilter}
          formatCurrency={formatCurrencyValue}
          startDate={formattedStartDate}
          endDate={formattedEndDate}
          workspaceId={workspaceId}
        />
      </Stack>
    </div>
  );
};

export default AiMlServicesSummary;
